import type { SkillsReportRider } from '../../../types/skills';

interface RiderSkillsListProps {
  riders: SkillsReportRider[];
  onRiderSelect: (riderId: string) => void;
}

/**
 * Stacked card layout listing each rider's skills data.
 * Mobile-first: one rider per row, data stacked vertically.
 * Riders are pre-sorted by the caller (sortRidersForDisplay).
 *
 * Requirements: 4.1, 4.2, 4.3, 4.4, 11.3, 12.1, 12.2, 12.3, 12.4
 */
export default function RiderSkillsList({ riders, onRiderSelect }: RiderSkillsListProps) {
  if (riders.length === 0) {
    return (
      <p className="py-4 text-center text-sm text-gray-500">
        No riders in this group.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {riders.map((rider) => (
        <div
          key={rider.rider_id}
          className="rounded-lg border border-gray-200 bg-white p-4"
        >
          {/* Tappable rider name */}
          <button
            type="button"
            onClick={() => onRiderSelect(rider.rider_id)}
            className="min-h-[44px] min-w-[44px] text-left text-base font-semibold text-blue-700 underline hover:text-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded"
          >
            {rider.name}
          </button>

          {/* Stacked data rows */}
          <div className="mt-2 space-y-2 text-base text-gray-700">
            {/* Skills Mastered */}
            <div className="flex justify-between">
              <span className="text-gray-500">Skills Mastered</span>
              <span className="font-medium">
                {rider.skills_score} / {rider.max_possible_score}
              </span>
            </div>

            {/* Skills percentage with progress bar */}
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-gray-500">Progress</span>
                <span className="font-medium">{rider.skills_percentage}%</span>
              </div>
              <div className="h-2 w-full rounded-full bg-gray-200">
                <div
                  className="h-2 rounded-full bg-blue-500"
                  style={{ width: `${Math.min(rider.skills_percentage, 100)}%` }}
                />
              </div>
            </div>

            {/* Assessed count */}
            <div className="flex justify-between">
              <span className="text-gray-500">Assessed</span>
              <span className="font-medium">
                {rider.assessed_count} / {rider.total_skills} skills
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

